import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { Modal, Tab, Tabs } from "react-bootstrap";
import { faRotate, faTrash } from "@fortawesome/free-solid-svg-icons";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import { proxyAPI } from "../proxyAPI";
import { BroadcastHandler } from "../Broadcast/BroadcastHandlers";
import { createEditorState, loadTagOptions } from "../helpers";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import {
  InboundCampaignType,
  useDeleteInboundCampaignMutation,
  useGetInboundCampaignQuery,
  useUpsertInboundCampignMutation,
} from "../store/inboundCampaignSlice";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { BroadcastHandlerType } from "../store/slices/broadcastSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isUserPermitted, pageProps } from "../helpers/pageProps";
import StyledTabContainer from "../Common/StyledTabContainer";
import InboundSelector from "./InboundSelector";
import HelpButton from "../HelpContent/HelpButton";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";
import { useDispatch } from "react-redux";
import { settingsProxyAPI } from "../store/slices/settingsSlice";

const {
  baseUrl: baseURL,
  csrfToken,
  defaultCountry,
  countries,
  emailSuffix,
  accountEmails,
} = JSON.parse((document.querySelector("#app") as any).dataset.page).props;

const newHandler: Partial<InboundCampaignType> = {
  Uuid: "",
  Label: "New Inbound Campaign",
  Description: "",
  IsWildcard: false,
  Inbound: "",
  Status: "active",
  Hits: 0,
  Channel: "sms",
  IsDeliveryStatus: false,
  Do: "no-action",
  ProjectId: undefined,
  ForwardMethod: "email",
  ForwardTo: "",
  DynamicHeader: "",
  UseNumber: "",
  UseThread: false,
  Message: {
    Plain: "",
    Rendered: "",
    EditorState: createEditorState(),
  },
  Reference: "",
  Category: "",
  Industry: "",
};

export default function InboundCampaignForm({
  uuid,
  showButton,
  onClose,
}: {
  uuid: string;
  showButton?: React.ReactNode;
  onClose?: () => void;
}) {
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const confirmDialog = useConfirmDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const [inboundCampaign, setInboundCampaign] =
    useState<Partial<InboundCampaignType>>(newHandler);
  const { data } = useGetInboundCampaignQuery(uuid || "", {
    skip: !uuid || uuid === "new",
  });
  useEffect(() => {
    if (!uuid) return;
    if (uuid === "new") {
      setInboundCampaign(newHandler);
      return;
    }
    if (data) {
      setInboundCampaign(data);
    }
  }, [uuid, data]);
  const [tab, setTab] = useState("1");
  const [loadingNewInbound, setLoadingNewInbound] = useState(false);
  const [projects, setProjects] = useState([]);

  const getInboundNumbers = async () => {
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/projects`,
      setLoading: () => null,
    });
    if (data) {
      setProjects(data);
    }
  };

  const handleClose = () => {
    setShow(false);
    setTab("1");
    onClose?.();
  };

  const [upsert] = useUpsertInboundCampignMutation();

  const handleSave = async () => {
    const res: any = await upsert(inboundCampaign);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(
      uuid
        ? "Inbound campaign saved successfully"
        : "Inbound campaign added successfully"
    );
    setInboundCampaign(res.data);
    dispatch(settingsProxyAPI.util.invalidateTags(["Tags"]));
  };

  const [deleteInboundCampaign] = useDeleteInboundCampaignMutation();
  const handleDelete = async () => {
    if (
      !uuid ||
      !(await confirmDialog(
        "Delete Inbound Campaign",
        `Are you sure you want to delete this Inbound Campaign?`
      ))
    ) {
      return;
    }
    const res: any = await deleteInboundCampaign(uuid);
    if (!res.error) {
      dispatch(settingsProxyAPI.util.invalidateTags(["Tags"]));
    }
  };

  const canEdit = isUserPermitted("use_inbound_campaign");

  return (
    <>
      {showButton && (
        <div
          className="h-100"
          onClick={() => {
            setShow(true);
          }}
        >
          {showButton}
        </div>
      )}
      <Modal
        show={showButton ? show : true}
        onHide={handleClose}
        size="lg"
        onShow={getInboundNumbers}
        contentClassName="bg-purple-gradient"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {uuid === "new" ? "Create A New" : "Edit"} Inbound Campaign
            <HelpButton helpKey="inbound-campaign-form-overview" />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!canEdit && (
            <div className="alert alert-secondary">
              You do not have permission to edit this inbound campaign.
            </div>
          )}
          <StyledTabContainer id="inbound-campaign-tabs">
            <Tabs
              id="controlled-tab-example"
              activeKey={tab}
              onSelect={(k: any) => setTab(k)}
              className="mb-3"
            >
              <Tab
                eventKey="1"
                title={
                  <span>
                    Select inbound <HelpButton helpKey="select-inbound" />
                  </span>
                }
              >
                <div className="row">
                  <div className="mb-3 col-6">
                    <InputLabel>
                      Title <HelpButton helpKey="inbound-campaign-title" />
                    </InputLabel>
                    <Input
                      label=""
                      value={[inboundCampaign.Label]}
                      onChange={([v]: any) =>
                        setInboundCampaign((p) => ({ ...p, Label: v }))
                      }
                      options={undefined}
                    />
                  </div>
                  <div className="mb-3 col-6">
                    <InputLabel>
                      Description{" "}
                      <HelpButton helpKey="inbound-campaign-description" />
                    </InputLabel>
                    <Input
                      inputType="textArea"
                      label=""
                      value={[inboundCampaign.Description]}
                      onChange={([v]: any) =>
                        setInboundCampaign((p) => ({ ...p, Description: v }))
                      }
                      options={undefined}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="mb-3 col-6">
                    <InputLabel>
                      Reference{" "}
                      <HelpButton helpKey="inbound-campaign-reference" />
                    </InputLabel>
                    <Input
                      label=""
                      value={[inboundCampaign.Reference]}
                      onChange={([v]: any) =>
                        setInboundCampaign((p) => ({ ...p, Reference: v }))
                      }
                      options={undefined}
                    />
                  </div>
                  <div className="mb-3 col-6">
                    <InputLabel>
                      Inbound Category
                      <HelpButton helpKey="inbound-campaign-category" />
                    </InputLabel>
                    <Input
                      inputType="select"
                      isValidNewOption={(o) => o}
                      value={[inboundCampaign.Category]}
                      onChange={([v]: any) =>
                        setInboundCampaign((p) => ({ ...p, Category: v }))
                      }
                      options={[
                        {
                          value: "",
                          label: `Default from product settings`,
                        },
                      ]}
                      loadOptions={loadTagOptions("category")}
                    />
                  </div>
                  <div className="mb-3 col-6">
                    <InputLabel>
                      Inbound Industry
                      <HelpButton helpKey="inbound-campaign-industry" />
                    </InputLabel>
                    <Input
                      inputType="select"
                      isValidNewOption={(o) => o}
                      value={[inboundCampaign.Industry]}
                      onChange={([v]: any) =>
                        setInboundCampaign((p) => ({ ...p, Industry: v }))
                      }
                      options={[
                        {
                          value: "",
                          label: `Default from product settings`,
                        },
                      ]}
                      loadOptions={loadTagOptions("industry")}
                    />
                  </div>
                </div>

                <InboundSelector
                  inboundCampaign={inboundCampaign}
                  setInboundCampaign={setInboundCampaign}
                />
                <div className="d-flex justify-content-end my-3 ">
                  <button
                    type="button"
                    className="btn btn-colored rounded-pill px-5 fw-bold"
                    onClick={() => setTab("2")}
                  >
                    NEXT
                  </button>
                </div>
              </Tab>
              <Tab
                eventKey="2"
                title={
                  <span>
                    Configure inbound handlers{" "}
                    <HelpButton helpKey="configure-inbound" />{" "}
                  </span>
                }
              >
                <Input
                  label="Status"
                  inputType="radio"
                  radioName="isActive"
                  value={[inboundCampaign.Status]}
                  onChange={([v]: any) => {
                    setInboundCampaign((p) => ({ ...p, Status: v }));
                  }}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                />
                <BroadcastHandler
                  isDeleteAllowed={false}
                  handlerType="inbound"
                  handler={inboundCampaign as BroadcastHandlerType}
                  setHandler={(h: any) =>
                    setInboundCampaign((p) => ({ ...p, ...h }))
                  }
                  deleteHandler={function (): void {}}
                />
                {/* <button
                  type="button"
                  className="d-flex btn p-0 align-items-center gap-2 mb-3"
                  onClick={() => {
                    setHandlers([...handlers, newHandler]);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="bg-colored text-white p-2 rounded-pill "
                  />
                  <span
                    className={`fw-bold ${
                      handlers.length === 0 ? "fs-3" : ""
                    } `}
                  >
                    ADD INBOUND HANDLER
                  </span>
                </button> */}
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-colored rounded-pill px-5 fw-bold"
                    onClick={() => setTab("1")}
                  >
                    BACK
                  </button>
                  <div className="flex-grow-1"></div>
                  <button
                    type="button"
                    className="btn btn-colored me-2 rounded-pill px-5 fw-bold text-uppercase"
                    onClick={handleDelete}
                    disabled={
                      !canEdit || !uuid || uuid === "new" || loadingNewInbound
                    }
                  >
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="text-white me-2"
                      size="lg"
                    />{" "}
                    Delete
                  </button>
                  <button
                    type="button"
                    disabled={
                      !canEdit ||
                      loadingNewInbound ||
                      inboundCampaign.Inbound === ""
                    }
                    className="btn btn-colored rounded-pill px-5 fw-bold text-uppercase"
                    style={{ height: 40 }}
                    onClick={handleSave}
                  >
                    {loadingNewInbound && (
                      <FontAwesomeIcon icon={faRotate} spin />
                    )}{" "}
                    {uuid ? "Save" : "Add inbound"}
                  </button>
                </div>
              </Tab>
            </Tabs>
          </StyledTabContainer>
          <JsonCollapse json={inboundCampaign} />
        </Modal.Body>
      </Modal>
    </>
  );
}
